import { gsap } from 'gsap';

const SPRING_CONFIG = {
    default: {
        type: 'elastic',
        amplitude: 0.25,
        period: 0.5,
    },
    subtle: {
        type: 'elastic',
        amplitude: 0.15,
        period: 0.4,
    },
    bouncy: {
        type: 'back',
        overshoot: 1.4,
    },
    smooth: {
        type: 'elastic',
        amplitude: 0.1,
        period: 0.35,
    },
    slow: {
        type: 'elastic',
        amplitude: 0.2,
        period: 0.6,
    },
    hero: {
        type: 'back',
        overshoot: 1.2,
    },
};

function getSpringEase(config = SPRING_CONFIG.default) {
    if (config.type === 'back') {
        return `back.out(${config.overshoot || 1.2})`;
    }
    return `elastic.out(${config.amplitude || 0.25}, ${config.period || 0.5})`;
}

const DEFAULT_HERO_CONFIG = {
    duration: 0.4,
    ease: 'power1.out',
    stagger: 0.15,
};

const HERO_ELEMENTS = {
    badge: '.HeroBadge',
    title: '.HeroTitle',
    subtitle: '.HeroSubtitle',
    content: '.HeroContent',
    actions: '.HeroActions',
    highlights: '.HeroFeatureHighlights',
    benefits: '.HeroBenefits',
    ctas: '.HeroCTAs',
    image: '.HeroImage',
    titleHighlight: '.TitleHighlight',
    promotionBadge: '.PromotionBadge',
    floatingBadge: '.FloatingPromotionBadge',
};

export const animations = {
    spring: {
        fadeIn(element, options = {}) {
            const {
                duration = 0.6,
                delay = 0,
                config = SPRING_CONFIG.default,
                onComplete = null,
            } = options;

            return gsap.fromTo(
                element,
                { opacity: 0 },
                {
                    opacity: 1,
                    duration,
                    delay,
                    ease: getSpringEase(config),
                    force3D: true,
                    onComplete,
                },
            );
        },

        fadeOut(element, options = {}) {
            const {
                duration = 0.4,
                delay = 0,
                config = SPRING_CONFIG.subtle,
                onComplete = null,
            } = options;

            return gsap.to(element, {
                opacity: 0,
                duration,
                delay,
                ease: 'power2.in',
                force3D: true,
                onComplete,
            });
        },

        slideIn(element, options = {}) {
            const {
                direction = 'up',
                distance = 30,
                duration = 0.7,
                delay = 0,
                config = SPRING_CONFIG.default,
                opacity = true,
                onComplete = null,
            } = options;

            const directions = {
                up: { y: distance, x: 0 },
                down: { y: -distance, x: 0 },
                left: { x: distance, y: 0 },
                right: { x: -distance, y: 0 },
            };

            const from = {
                ...directions[direction],
                opacity: opacity ? 0 : 1,
            };

            const to = {
                x: 0,
                y: 0,
                opacity: opacity ? 1 : undefined,
                duration,
                delay,
                ease: getSpringEase(config),
                force3D: true,
                onComplete,
            };

            if (!opacity) {
                delete to.opacity;
            }

            return gsap.fromTo(element, from, to);
        },

        slideOut(element, options = {}) {
            const {
                direction = 'down',
                distance = 30,
                duration = 0.4,
                delay = 0,
                onComplete = null,
            } = options;

            const directions = {
                up: { y: -distance, x: 0 },
                down: { y: distance, x: 0 },
                left: { x: -distance, y: 0 },
                right: { x: distance, y: 0 },
            };

            return gsap.to(element, {
                ...directions[direction],
                opacity: 0,
                duration,
                delay,
                ease: 'power2.in',
                force3D: true,
                onComplete,
            });
        },

        scaleIn(element, options = {}) {
            const {
                scale = 0.8,
                duration = 0.6,
                delay = 0,
                config = SPRING_CONFIG.default,
                opacity = true,
                onComplete = null,
            } = options;

            return gsap.fromTo(
                element,
                {
                    scale,
                    opacity: opacity ? 0 : 1,
                },
                {
                    scale: 1,
                    opacity: opacity ? 1 : undefined,
                    duration,
                    delay,
                    ease: getSpringEase(config),
                    transformOrigin: 'center center',
                    force3D: true,
                    onComplete,
                },
            );
        },

        scaleOut(element, options = {}) {
            const { scale = 0.8, duration = 0.4, delay = 0, onComplete = null } = options;

            return gsap.to(element, {
                scale,
                opacity: 0,
                duration,
                delay,
                ease: 'power2.in',
                transformOrigin: 'center center',
                force3D: true,
                onComplete,
            });
        },

        bounceIn(element, options = {}) {
            const {
                duration = 0.8,
                delay = 0,
                config = SPRING_CONFIG.bouncy,
                onComplete = null,
            } = options;

            return gsap.fromTo(
                element,
                {
                    scale: 0.3,
                    opacity: 0,
                },
                {
                    scale: 1,
                    opacity: 1,
                    duration,
                    delay,
                    ease: getSpringEase(config),
                    transformOrigin: 'center center',
                    force3D: true,
                    onComplete,
                },
            );
        },

        rotateIn(element, options = {}) {
            const {
                rotation = 15,
                duration = 0.7,
                delay = 0,
                config = SPRING_CONFIG.default,
                opacity = true,
                onComplete = null,
            } = options;

            return gsap.fromTo(
                element,
                {
                    rotation,
                    opacity: opacity ? 0 : 1,
                },
                {
                    rotation: 0,
                    opacity: opacity ? 1 : undefined,
                    duration,
                    delay,
                    ease: getSpringEase(config),
                    transformOrigin: 'center center',
                    force3D: true,
                    onComplete,
                },
            );
        },

        staggerIn(elements, options = {}) {
            const {
                animation = 'fadeIn',
                stagger = 0.1,
                duration = 0.6,
                delay = 0,
                config = SPRING_CONFIG.default,
                onComplete = null,
            } = options;

            const elementArray = Array.isArray(elements) ? elements : Array.from(elements);

            const tl = gsap.timeline({
                defaults: {
                    ease: getSpringEase(config),
                    duration,
                    force3D: true,
                },
                delay,
                onComplete,
            });

            elementArray.forEach((el, index) => {
                const animOptions = { duration, config, delay: index * stagger };
                if (animation === 'fadeIn') {
                    tl.fromTo(el, { opacity: 0 }, { opacity: 1, force3D: true }, index * stagger);
                } else if (animation === 'slideIn') {
                    tl.fromTo(
                        el,
                        { y: 20, opacity: 0 },
                        { y: 0, opacity: 1, force3D: true },
                        index * stagger,
                    );
                } else if (animation === 'scaleIn') {
                    tl.fromTo(
                        el,
                        { scale: 0.8, opacity: 0 },
                        { scale: 1, opacity: 1, transformOrigin: 'center center', force3D: true },
                        index * stagger,
                    );
                }
            });

            return tl;
        },
    },

    smooth: {
        fadeIn(element, options = {}) {
            const { duration = 0.4, delay = 0, onComplete = null } = options;
            return gsap.fromTo(
                element,
                { opacity: 0 },
                {
                    opacity: 1,
                    duration,
                    delay,
                    ease: 'power2.out',
                    force3D: true,
                    onComplete,
                },
            );
        },

        slideIn(element, options = {}) {
            const {
                direction = 'up',
                distance = 20,
                duration = 0.5,
                delay = 0,
                opacity = true,
                onComplete = null,
            } = options;

            const directions = {
                up: { y: distance, x: 0 },
                down: { y: -distance, x: 0 },
                left: { x: distance, y: 0 },
                right: { x: -distance, y: 0 },
            };

            const from = {
                ...directions[direction],
                opacity: opacity ? 0 : 1,
            };

            const to = {
                x: 0,
                y: 0,
                opacity: opacity ? 1 : undefined,
                duration,
                delay,
                ease: 'power2.out',
                force3D: true,
                onComplete,
            };

            if (!opacity) {
                delete to.opacity;
            }

            return gsap.fromTo(element, from, to);
        },
    },
};

export const springConfig = SPRING_CONFIG;

export const heroAnimationMixin = {
    methods: {
        animateHero(options = {}) {
            const animation = animateHero(options);
            this.$once('hook:beforeDestroy', () => {
                animation.kill();
            });
            return animation;
        },
    },
};

export function createSpringAnimation(element, properties, options = {}) {
    const {
        duration = 0.6,
        delay = 0,
        config = SPRING_CONFIG.default,
        onComplete = null,
        onUpdate = null,
    } = options;

    const ease = getSpringEase(config);

    return gsap.to(element, {
        ...properties,
        duration,
        delay,
        ease,
        force3D: true,
        onComplete,
        onUpdate,
    });
}

export function createSpringTimeline(options = {}) {
    const {
        defaults = {
            ease: getSpringEase(SPRING_CONFIG.default),
            duration: 0.6,
            force3D: true,
        },
        delay = 0,
        onComplete = null,
    } = options;

    return gsap.timeline({
        defaults,
        delay,
        onComplete,
    });
}

export function animateWords(element, options = {}) {
    const {
        stagger = 0.08,
        duration = 0.8,
        delay = 0,
        config = SPRING_CONFIG.hero,
        yOffset = 30,
        opacity = true,
        onComplete = null,
    } = options;

    if (!element) return null;

    const text = element.textContent || element.innerText;
    const words = text.split(/\s+/).filter((word) => word.length > 0);

    if (words.length === 0) return null;

    const ease = getSpringEase(config);
    const wordElements = [];

    words.forEach((word, index) => {
        const span = document.createElement('span');
        span.textContent = word;
        span.style.display = 'inline-block';
        span.style.opacity = opacity ? '0' : '1';
        span.style.transform = `translateY(${yOffset}px)`;
        span.style.willChange = 'transform, opacity';
        wordElements.push(span);
    });

    const fragment = document.createDocumentFragment();
    words.forEach((word, index) => {
        fragment.appendChild(wordElements[index]);
        if (index < words.length - 1) {
            fragment.appendChild(document.createTextNode(' '));
        }
    });

    element.innerHTML = '';
    element.appendChild(fragment);

    const tl = gsap.timeline({
        delay,
        onComplete,
    });

    wordElements.forEach((wordEl, index) => {
        tl.to(
            wordEl,
            {
                y: 0,
                opacity: opacity ? 1 : undefined,
                duration,
                ease,
                force3D: true,
            },
            index * stagger,
        );
    });

    return tl;
}

function setupWordAnimation(titleElement, titleHighlightElement) {
    if (titleHighlightElement) {
        gsap.set(titleHighlightElement, { opacity: 0 });
    }

    const walker = document.createTreeWalker(titleElement, NodeFilter.SHOW_TEXT, null, false);

    const textNodes = [];
    let node;
    while ((node = walker.nextNode())) {
        if (node.textContent.trim()) {
            textNodes.push(node);
        }
    }

    textNodes.forEach((textNode) => {
        const text = textNode.textContent;
        const words = text.split(/(\s+)/).filter((part) => part.length > 0);
        const fragment = document.createDocumentFragment();

        words.forEach((word) => {
            if (word.trim()) {
                const span = document.createElement('span');
                span.style.display = 'inline-block';
                span.style.opacity = '0';
                span.style.transform = 'translateY(40px)';
                span.style.filter = 'blur(20px)';
                span.style.willChange = 'transform, opacity, filter';
                span.textContent = word;
                fragment.appendChild(span);
            } else {
                fragment.appendChild(document.createTextNode(word));
            }
        });

        textNode.parentNode.replaceChild(fragment, textNode);
    });
}

function animateElement(timeline, selector, config, timing) {
    const element = document.querySelector(selector);
    if (!element) return;

    timeline.to(selector, config, timing);
}

export function animateHero(options = {}) {
    const {
        elements = {},
        useSpring = true,
        springConfigType = 'hero',
        animateTitleWords = false,
        onComplete = null,
    } = options;

    const config = useSpring ? SPRING_CONFIG[springConfigType] : null;
    const ease = useSpring ? getSpringEase(config) : DEFAULT_HERO_CONFIG.ease;

    const selectors = {
        badge: elements.badge || null,
        title: elements.title || HERO_ELEMENTS.title,
        subtitle: elements.subtitle || HERO_ELEMENTS.subtitle,
        content: elements.content || null,
        actions: elements.actions || null,
        highlights: elements.highlights || HERO_ELEMENTS.highlights || '.FeatureHighlights',
        benefits: elements.benefits || HERO_ELEMENTS.benefits,
        ctas: elements.ctas || HERO_ELEMENTS.ctas,
        image: elements.image || HERO_ELEMENTS.image,
        titleHighlight: elements.titleHighlight || HERO_ELEMENTS.titleHighlight,
        promotionBadge: elements.promotionBadge || HERO_ELEMENTS.promotionBadge,
        floatingBadge: elements.floatingBadge || HERO_ELEMENTS.floatingBadge,
    };

    const allSelectors = Object.values(selectors)
        .filter(Boolean)
        .filter((sel) => sel.startsWith('.'))
        .join(', ');

    gsap.killTweensOf(allSelectors);

    const titleElement = selectors.title ? document.querySelector(selectors.title) : null;
    const titleHighlightElement = selectors.titleHighlight
        ? document.querySelector(selectors.titleHighlight)
        : null;

    const initialStates = {
        [selectors.content]: { y: 15, opacity: 0 },
        [selectors.badge]: { opacity: 0 },
        [selectors.promotionBadge]: { opacity: 0 },
        [selectors.titleHighlight]: { opacity: 0 },
        [selectors.subtitle]: { y: 15, opacity: 0 },
        [selectors.actions]: { y: 8, opacity: 0 },
        [selectors.highlights]: { y: 10, opacity: 0 },
        [selectors.benefits]: { y: 10, opacity: 0 },
        [selectors.ctas]: { y: 8, opacity: 0 },
        [selectors.image]: { opacity: 0 },
        [selectors.floatingBadge]: { opacity: 0, scale: 0.8, rotation: -10 },
    };

    if (animateTitleWords && titleElement) {
        setupWordAnimation(titleElement, titleHighlightElement);
    } else {
        initialStates[selectors.title] = { y: 40, opacity: 0, filter: 'blur(40px)' };
    }

    Object.entries(initialStates).forEach(([selector, state]) => {
        if (selector && document.querySelector(selector)) {
            gsap.set(selector, state);
        }
    });

    const masterTl = useSpring
        ? createSpringTimeline({
              defaults: {
                  ease,
                  duration: DEFAULT_HERO_CONFIG.duration + 0.4,
                  force3D: true,
              },
              onComplete,
          })
        : gsap.timeline({
              defaults: {
                  ease,
                  duration: DEFAULT_HERO_CONFIG.duration,
                  force3D: true,
              },
              onComplete,
          });

    const badgeConfig = { opacity: 1, duration: 0.7, ease, force3D: true };
    if (selectors.titleHighlight && titleHighlightElement) {
        masterTl.to(selectors.titleHighlight, badgeConfig);
    }
    if (selectors.badge) {
        animateElement(masterTl, selectors.badge, badgeConfig);
    }
    if (selectors.promotionBadge) {
        animateElement(masterTl, selectors.promotionBadge, badgeConfig);
    }

    let headlineEndTime = 0;

    if (animateTitleWords && titleElement) {
        const wordSpans = titleElement.querySelectorAll('span:not(.TitleHighlight)');
        if (wordSpans.length > 0) {
            const wordStagger = 0.05;
            const wordDuration = 1;

            wordSpans.forEach((span, index) => {
                const startTime = index * wordStagger;
                masterTl.to(
                    span,
                    {
                        y: 0,
                        opacity: 1,
                        filter: 'blur(0px)',
                        duration: wordDuration,
                        ease,
                        force3D: true,
                    },
                    startTime,
                );
                headlineEndTime = startTime + wordDuration;
            });
        }
    } else if (selectors.title && titleElement) {
        masterTl.to(
            selectors.title,
            {
                y: 0,
                opacity: 1,
                filter: 'blur(0px)',
                duration: 1.0,
                ease,
                force3D: true,
            },
            '-=0.6',
        );
        headlineEndTime = 1.0;
    }

    const elementAnimations = [
        {
            selector: selectors.subtitle,
            offset: -0.7,
            fallback: '-=0.55',
            props: { y: 0, opacity: 1, force3D: true },
            duration: 0.75,
        },
        {
            selector: selectors.actions,
            offset: -0.5,
            fallback: '-=0.45',
            props: { y: 0, opacity: 1, force3D: true },
            duration: 0.7,
        },
        {
            selector: selectors.highlights,
            offset: -0.25,
            fallback: '-=0.45',
            props: { y: 0, opacity: 1, force3D: true },
            duration: 0.7,
        },
        {
            selector: selectors.benefits,
            offset: -0.1,
            fallback: '-=0.45',
            props: { y: 0, opacity: 1, force3D: true },
            duration: 0.7,
        },
        {
            selector: selectors.ctas,
            offset: -0.1,
            fallback: '-=0.45',
            props: { y: 0, opacity: 1, force3D: true },
            duration: 0.7,
        },
        {
            selector: selectors.image,
            offset: -0.25,
            fallback: '-=0.35',
            props: { opacity: 1, force3D: true },
            duration: 0.7,
        },
        {
            selector: selectors.floatingBadge,
            offset: -0.15,
            fallback: '-=0.25',
            props: { opacity: 1, scale: 1, rotation: 0, force3D: true },
            duration: 0.75,
            ease: useSpring ? ease : 'back.out(1.7)',
        },
    ];

    elementAnimations.forEach(
        ({ selector, offset, fallback, props, duration, ease: customEase }) => {
            if (!selector) return;

            const timing =
                animateTitleWords && headlineEndTime > 0 ? headlineEndTime + offset : fallback;

            animateElement(
                masterTl,
                selector,
                {
                    ...props,
                    duration,
                    ease: customEase || ease,
                },
                timing,
            );
        },
    );

    return {
        timeline: masterTl,
        kill: () => {
            gsap.killTweensOf(allSelectors);
        },
    };
}

animations.modal = {
    animateIn(backdrop, content, options = {}) {
        const {
            backdropDuration = 0.5,
            contentDuration = 0.6,
            useSpring = false,
            onComplete = null,
        } = options;

        const tl = gsap.timeline({
            defaults: {
                ease: useSpring ? getSpringEase(SPRING_CONFIG.default) : 'power3.out',
                force3D: true,
                transformOrigin: 'center center',
            },
            onComplete,
        });

        tl.fromTo(
            backdrop,
            { autoAlpha: 0 },
            {
                autoAlpha: 1,
                duration: backdropDuration,
                ease: 'power2.inOut',
                force3D: true,
            },
        ).fromTo(
            content,
            {
                y: 60,
                scale: 0.9,
                autoAlpha: 0,
                rotationX: 5,
                transformOrigin: 'center center',
            },
            {
                y: 0,
                scale: 1,
                autoAlpha: 1,
                rotationX: 0,
                duration: contentDuration,
                ease: useSpring ? getSpringEase(SPRING_CONFIG.bouncy) : 'back.out(1.7)',
                force3D: true,
            },
            '-=0.4',
        );

        return tl;
    },

    animateOut(backdrop, content, options = {}) {
        const { duration = 0.3, onComplete = null } = options;

        const tl = gsap.timeline({
            defaults: {
                ease: 'power3.in',
                force3D: true,
            },
            onComplete,
        });

        tl.to(content, {
            y: 30,
            scale: 0.95,
            autoAlpha: 0,
            duration,
            force3D: true,
        }).to(
            backdrop,
            {
                autoAlpha: 0,
                duration: duration * 0.8,
                force3D: true,
            },
            '-=0.2',
        );

        return tl;
    },
};

animations.drawer = {
    slideIn(element, options = {}) {
        const {
            direction = 'right',
            duration = 0.4,
            useSpring = false,
            onComplete = null,
        } = options;

        const fromStates = {
            right: { x: '100%' },
            left: { x: '-100%' },
            top: { y: '-100%' },
            bottom: { y: '100%' },
        };

        const from = fromStates[direction] || fromStates.right;

        return gsap.fromTo(
            element,
            { ...from, autoAlpha: 0 },
            {
                x: 0,
                y: 0,
                autoAlpha: 1,
                duration,
                ease: useSpring ? getSpringEase(SPRING_CONFIG.default) : 'power3.out',
                onComplete,
            },
        );
    },

    slideOut(element, options = {}) {
        const { direction = 'right', duration = 0.3, onComplete = null } = options;

        const directions = {
            right: { x: '100%' },
            left: { x: '-100%' },
            top: { y: '-100%' },
            bottom: { y: '100%' },
        };

        return gsap.to(element, {
            ...directions[direction],
            autoAlpha: 0,
            duration,
            ease: 'power3.in',
            onComplete,
        });
    },
};
